
/**
 * Escapes a value for HTML text or a quoted attribute (C-17).
 * @param {unknown} value
 * @returns {string}
 */
const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * QuickViewMultiOption – Custom Element
 *
 * Shopify supports up to 3 product options. To exceed this limit, multiple
 * logical options (e.g. Quality, Weight, Metal) are packed into one Shopify
 * option using "/" as a delimiter:
 *   option name  → "Quality/Weight/Metal"
 *   option value → "A/1.85/Rose Gold Over Sterling Silver"
 *
 * This component unpacks those combined options, renders one fieldset per
 * logical option, and cascades availability based on upstream selections.
 * 
 * Design matches the existing quick-view-content.liquid structure:
 * - First option: Image swatches from variant images
 * - Metal option: Color swatches from CDN URL built from value name
 *   (https://image.shoplc.com/groupingproductimages/<value>.png, with 2-letter fallback on 404)
 * - Size/other options: Size buttons
 * - Ring products: Size chart link next to Size option
 * 
 * Adapted from product-multi-option.js for quick view drawer usage.
 */
class QuickViewMultiOption extends HTMLElement {

  /** @type {DocumentFragment | null} */
  #optionTemplate = null;

  /** @type {DocumentFragment | null} */
  #imageSwatchTemplate = null;

  /** @type {DocumentFragment | null} */
  #colorSwatchTemplate = null;

  /** @type {DocumentFragment | null} */
  #sizeButtonTemplate = null;

  /** @type {AbortController | null} */
  #abortController = null;

  constructor() {
    super();

    /** @type {any} */
    this.data = null;
    /** @type {string[]} Logical option names after splitting "/" packed names */
    this.optionNames = [];
    /** @type {string[]} Selected value per logical option (parallel to optionNames) */
    this.selectedValues = [];

    this.shopDomain = this.getAttribute('data-shop-domain') || window.Shopify?.shop || location.hostname;
    this.productIdGid = this.getAttribute('data-product-gid');
    this.productId = this.getAttribute('data-product-id');
    /** @type {string} Comma-separated initial selected values injected by Liquid */
    this.initialSelectedValues = this.getAttribute('data-selected-value') || '';
    /** @type {boolean} Whether this is a ring product (for size chart) */
    this.isRingProduct = this.getAttribute('data-is-ring-product') === 'true';
  }

  /**
   * Build the CDN image URL for a metal option value.
   * Rule: trim whitespace, replace "/" and spaces with "_", preserve case and other chars.
   * @param {string} value
   * @returns {string}
   */
  #buildMetalImageUrl(value) {
    const filename = String(value).trim().replace(/\//g, '_').replace(/ /g, '_');
    return `https://image.shoplc.com/groupingproductimages/${filename}.png`;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  async connectedCallback() {
    await this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async #init() {
    if (!this.productId) return;

    const templateCacheKey = 'qvmo_templates_v2';
    const win = window;

    if (!win[templateCacheKey]) {
      const optionTemplateEl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('#quick-view-option-template'));
      const imageSwatchTemplateEl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('#quick-view-image-swatch-template'));
      const colorSwatchTemplateEl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('#quick-view-color-swatch-template'));
      const sizeButtonTemplateEl = /** @type {HTMLTemplateElement | null} */ (this.querySelector('#quick-view-size-button-template'));

      if (optionTemplateEl && imageSwatchTemplateEl && colorSwatchTemplateEl && sizeButtonTemplateEl) {
        win[templateCacheKey] = {
          option: optionTemplateEl.content.cloneNode(true),
          imageSwatch: imageSwatchTemplateEl.content.cloneNode(true),
          colorSwatch: colorSwatchTemplateEl.content.cloneNode(true),
          sizeButton: sizeButtonTemplateEl.content.cloneNode(true)
        };
      } else {
        return;
      }
    }

    this.#optionTemplate = /** @type {DocumentFragment} */ (win[templateCacheKey].option.cloneNode(true));
    this.#imageSwatchTemplate = /** @type {DocumentFragment} */ (win[templateCacheKey].imageSwatch.cloneNode(true));
    this.#colorSwatchTemplate = /** @type {DocumentFragment} */ (win[templateCacheKey].colorSwatch.cloneNode(true));
    this.#sizeButtonTemplate = /** @type {DocumentFragment} */ (win[templateCacheKey].sizeButton.cloneNode(true));

    const dataCacheKey = `qvmo_${this.productId}`;
    /** @type {any} */
    const winTyped = win;
    this.data = winTyped[dataCacheKey] || (winTyped[dataCacheKey] = await this.fetchAllProductData());

    if (this.data) {
      this.optionNames = this.data.optionNames;
      this.#parseInitialSelection();
      this.render();
    }
  }

  // ── Data Fetching ────────────────────────────────────────────────────────────

  /**
   * Fetches all options and variants via the proxy API (same as product-multi-option).
   * @returns {Promise<any>}
   */
  async fetchAllProductData() {
    /** @type {any[]} */
    let allVariants = [];
    /** @type {string[]} */
    let optionNames = [];
    /** @type {Record<string,string>} */
    let optionsMap = {};
    /** @type {Record<string,any>} */
    let swatchMap = {};
    /** @type {Record<string,any>} */
    let valueMetadata = {};
    /** @type {number[]} */
    let multiOptionIndices = [];

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(`/apps/common/v1/multi-options?product=${this.productId}`, {
        signal: this.#abortController.signal
      });
      const result = await response.json();

      if (!result.success) throw new Error(result.message || 'API request failed');

      const product = result.data;
      if (!product) return null;

      if (product.options) {
        this.#parseOptionLabels(product.options, optionNames, optionsMap, swatchMap, valueMetadata, multiOptionIndices);
      }

      if (product.variants?.nodes) {
        this.#accumulateVariants(product.variants.nodes, allVariants, swatchMap);
      }

      return { optionNames, variants: allVariants, optionsMap, swatchMap, valueMetadata, multiOptionIndices };

    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('QuickViewMultiOption – fetch error:', e);
      }
      return null;
    }
  }

  #parseOptionLabels(shopifyOptions, optionNames, optionsMap, swatchMap, valueMetadata, multiOptionIndices) {
    shopifyOptions.forEach((/** @type {any} */ opt, /** @type {number} */ optIndex) => {
      const isMulti = opt.name.includes('/');
      const logicalNames = isMulti ? opt.name.split('/') : [opt.name];

      if (isMulti) {
        logicalNames.forEach((/** @type {string} */ _, /** @type {number} */ i) => multiOptionIndices.push(optionNames.length + i));
      }

      optionNames.push(...logicalNames);
      logicalNames.forEach((/** @type {string} */ name) => { if (!valueMetadata[name]) valueMetadata[name] = {}; });

      opt.optionValues.forEach((/** @type {any} */ ov) => {
        const numericId = ov.id.split('/').pop();
        const variantId = ov.firstSelectableVariant?.id.split('/').pop();
        optionsMap[ov.name] = numericId || '';

        if (isMulti) {
          const valueParts = ov.name.split('/');
          valueParts.forEach((/** @type {string} */ part, /** @type {number} */ i) => {
            if (!valueMetadata[logicalNames[i]][part]) {
              valueMetadata[logicalNames[i]][part] = { id: numericId, variantId, position: optIndex + 1 };
            }
          });
          const firstPart = valueParts[0];
          if (ov.swatch && !swatchMap[firstPart]) {
            swatchMap[firstPart] = { image: ov.swatch.image?.url, color: ov.swatch.color };
          }
        } else {
          valueMetadata[opt.name][ov.name] = { id: numericId, variantId, position: optIndex + 1 };
        }
      });
    });
  }

  #accumulateVariants(nodes, allVariants, swatchMap) {
    nodes.forEach((/** @type {any} */ node) => {
      const variantId = node.id.split('/').pop();
      /** @type {string[]} */
      const splitValues = [];
      node.selectedOptions.forEach((/** @type {any} */ so) => {
        so.name.includes('/') ? splitValues.push(...so.value.split('/')) : splitValues.push(so.value);
      });

      if (splitValues[0] && !swatchMap[splitValues[0]] && node.image) {
        swatchMap[splitValues[0]] = { image: node.image.url };
      }

      allVariants.push({
        id: variantId,
        sku: node.sku || '',
        available: node.availableForSale,
        splitValues,
        option: node.selectedOptions.map((/** @type {any} */ so) => so.value).join(' / '),
        image: node.image?.url || null
      });
    });
  }

  #parseInitialSelection() {
    if (this.initialSelectedValues && this.optionNames.length > 0) {
      /** @type {string[]} */
      const flatValues = [];
      this.initialSelectedValues.split(',').forEach((/** @type {string} */ part) => {
        part.includes('/') ? flatValues.push(...part.split('/')) : flatValues.push(part);
      });
      this.selectedValues = new Array(this.optionNames.length).fill('');
      flatValues.forEach((val, i) => { if (i < this.selectedValues.length) this.selectedValues[i] = val; });
    } else {
      this.selectedValues = new Array(this.optionNames.length).fill('');
    }
  }

  // ── Rendering ────────────────────────────────────────────────────────────────

  render() {
    const mount = this.querySelector('.quick-view-multi-option__ui-mount');
    if (!mount || !this.data) return;

    mount.innerHTML = '';
    const container = document.createElement('div');
    container.className = 'quick-view-multi-option__container quick-view__variant-selector';
    container.setAttribute('data-variant-selector', '');

    this.optionNames.forEach((name, index) => {
      const fieldset = this.#createFieldset(name, index);
      if (fieldset) container.appendChild(fieldset);
    });

    mount.appendChild(container);
  }

  #getOptionType(name, index) {
    const nameLower = name.toLowerCase();
    if (nameLower === 'size') return 'size-button';
    if (nameLower === 'metal') return 'color-swatch';
    if (nameLower === 'color') return 'image-swatch';
    if (index === 0) return 'image-swatch';
    return 'size-button';
  }

  #getClassPrefix(optionType) {
    return optionType === 'color-swatch' ? 'quick-view__color-swatch' : 
           optionType === 'size-button' ? 'quick-view__size-button' : 'quick-view__swatch';
  }

  #createFieldset(name, index) {
    if (!this.#optionTemplate) return null;

    const fragment = /** @type {DocumentFragment} */ (this.#optionTemplate.cloneNode(true));
    const optionDiv = /** @type {HTMLElement | null} */ (fragment.querySelector('.quick-view__option'));
    const nameSpan = fragment.querySelector('.quick-view-multi-option__name');
    const valSpan = fragment.querySelector('.quick-view-multi-option__swatch-value');
    const valuesContainer = fragment.querySelector('[data-values-container]');
    const sizeChartLink = fragment.querySelector('[data-size-chart-link]');

    if (!optionDiv || !nameSpan || !valSpan || !valuesContainer) return null;

    optionDiv.dataset.fieldsetIndex = index.toString();
    optionDiv.setAttribute('ref', 'fieldsets[]');
    // Distinct classes per option row (by name and by position) so each one
    // — Quality, Weight, Metal, Size, etc. — can be styled/adjusted on its
    // own instead of only as a group via the shared .quick-view__option class.
    const nameSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (nameSlug) optionDiv.classList.add(`quick-view__option--${nameSlug}`);
    optionDiv.classList.add(`quick-view__option--index-${index}`);
    nameSpan.textContent = name;
    valSpan.textContent = this.selectedValues[index] || '';

    const optionType = this.#getOptionType(name, index);
    const classPrefix = this.#getClassPrefix(optionType);
    const containerClass = optionType === 'image-swatch' ? 'quick-view__swatches' :
                          optionType === 'color-swatch' ? 'quick-view__color-swatches' :
                          'quick-view__size-buttons';

    valuesContainer.classList.add(containerClass);
    valuesContainer.setAttribute('aria-label', `Select ${name}`);

    if (name.toLowerCase() === 'size' && this.isRingProduct && sizeChartLink) {
      sizeChartLink.classList.remove('hidden');
    }

    const isLastOption = index === this.optionNames.length - 1;
    this.#getUniqueValuesAtIndex(index).forEach(val => {
      const valEl = this.#createSwatch(name, val, index, optionType, isLastOption);
      if (valEl) valuesContainer.appendChild(valEl);
    });

    return optionDiv;
  }

  #getUniqueValuesAtIndex(index) {
    if (!this.data) return [];
    const eligible = this.data.variants.filter((/** @type {any} */ v) => {
      for (let i = 0; i < index; i++) {
        if (this.selectedValues[i] !== '' && this.selectedValues[i] !== v.splitValues[i]) return false;
      }
      return true;
    });
    return [...new Set(eligible.map(v => v.splitValues[index]))].filter(Boolean);
  }

  #getVariantSkuForSwatch(value, index) {
    if (!this.data?.variants) return '';
    const testSelection = [...this.selectedValues];
    testSelection[index] = value;
    const matchingVariant = this.data.variants.find((/** @type {any} */ v) =>
      v.splitValues.length === testSelection.length &&
      v.splitValues.every((/** @type {string} */ sv, /** @type {number} */ i) => testSelection[i] !== '' && sv === testSelection[i])
    );
    return matchingVariant?.sku || '';
  }

  #createSwatch(optionName, value, index, optionType, isLastOption) {
    const template = optionType === 'image-swatch' ? this.#imageSwatchTemplate :
                    optionType === 'color-swatch' ? this.#colorSwatchTemplate :
                    this.#sizeButtonTemplate;

    if (!template || !this.data) return null;

    const fragment = /** @type {DocumentFragment} */ (template.cloneNode(true));
    const wrapper = /** @type {HTMLElement | null} */ (fragment.querySelector('.quick-view__swatch-wrapper'));
    const label = fragment.querySelector('label');
    const input = /** @type {HTMLInputElement | null} */ (fragment.querySelector('input'));
    const icon = fragment.querySelector('.quick-view__swatch-icon--ring');

    if (!wrapper || !label || !input) return null;

    // Same per-option class hooks as the fieldset itself, so an individual
    // swatch can be styled based on which option row (Quality/Weight/Metal/
    // Size, etc.) it belongs to.
    const optionNameSlug = optionName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (optionNameSlug) wrapper.classList.add(`quick-view__swatch-wrapper--${optionNameSlug}`);
    wrapper.classList.add(`quick-view__swatch-wrapper--index-${index}`);

    const metadata = this.data.valueMetadata?.[optionName]?.[value] || {};
    const isChecked = this.selectedValues[index] === value;
    const isAvailable = this.#checkAvailability(value, index);
    const classPrefix = this.#getClassPrefix(optionType);

    // Set common label attributes
    label.setAttribute('data-option-value', value);
    label.setAttribute('data-option-position', (index + 1).toString());
    if (isLastOption) {
      label.setAttribute('data-last-option-swatch', '');
      const variantSku = this.#getVariantSkuForSwatch(value, index);
      if (variantSku) label.setAttribute('data-variant-sku', variantSku);
    }
    if (isChecked) label.classList.add(`${classPrefix}--selected`);
    if (!isAvailable) label.classList.add(`${classPrefix}--unavailable`, 'swatch-unavailable');

    // Set common input attributes
    input.name = `quick-view-option-${optionName}-${this.productId || 'unknown'}`;
    input.value = value;
    input.checked = isChecked;
    input.setAttribute('data-option-position', (index + 1).toString());
    input.setAttribute('data-option-value-id', metadata.id || '');
    input.setAttribute('data-option-value-available', isAvailable.toString());
    input.setAttribute('aria-label', `${optionName}: ${value}${!isAvailable ? ' (Unavailable)' : ''}`);
    if (!isAvailable) input.setAttribute('aria-disabled', 'true');

    // Set type-specific content
    if (optionType === 'image-swatch') {
      const img = fragment.querySelector('img');
      if (img) {
        const swatchData = this.data.swatchMap[value];
        const imageUrl = swatchData?.image;
        if (imageUrl) {
          const resizedUrl = imageUrl.includes('?') ? `${imageUrl.split('?')[0]}?width=100` : `${imageUrl}?width=100`;
          img.src = resizedUrl;
          img.alt = value;
        } else {
          const imgParent = img.parentElement;
          if (imgParent) {
            imgParent.innerHTML = `<span class="quick-view__swatch-text">${escapeHtml(value.slice(0, 2).toUpperCase())}</span>`;
          }
        }
      }
    } else if (optionType === 'color-swatch') {
      const swatchEl = /** @type {HTMLElement | null} */ (fragment.querySelector('[data-swatch-element]'));
      if (swatchEl) {
        const fallbackInitials = value.slice(0, 2).toUpperCase();
        const isMetal = optionName.toLowerCase() === 'metal';

        if (isMetal) {
          const imageUrl = this.#buildMetalImageUrl(value);
          swatchEl.classList.add('swatch--metal-url');
          swatchEl.innerHTML = `
            <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(value)}" class="swatch__image" loading="lazy" width="80" height="80">
            <span class="swatch-variant__name swatch__fallback-initials" style="display:none;">${escapeHtml(fallbackInitials)}</span>
          `;
          const img = swatchEl.querySelector('img');
          const fallback = swatchEl.querySelector('.swatch__fallback-initials');
          if (img && fallback) {
            img.addEventListener('error', () => {
              img.style.display = 'none';
              /** @type {HTMLElement} */ (fallback).style.display = 'flex';
            }, { once: true });
          }
        } else {
          swatchEl.innerHTML = `<span class="swatch-variant__name">${escapeHtml(fallbackInitials)}</span>`;
        }
      }
    } else {
      const text = fragment.querySelector('.quick-view__size-text');
      if (text) text.textContent = value;
    }

    // Show sold-out icon if unavailable
    if (!isAvailable && icon) icon.classList.remove('hidden');

    // Add event listener
    input.addEventListener('change', () => {
      this.selectedValues[index] = value;
      this.#updateSwatchSelection(index);
      this.#handleSelectionChange();
    });

    return wrapper;
  }

  #updateSwatchSelection(index) {
    const optionDiv = this.querySelector(`[data-fieldset-index="${index}"]`);
    if (!optionDiv) return;

    const optionName = this.optionNames[index] || '';
    const optionType = this.#getOptionType(optionName, index);
    const classPrefix = this.#getClassPrefix(optionType);

    optionDiv.querySelectorAll('label').forEach(lbl => {
      lbl.classList.remove(`${classPrefix}--selected`);
    });

    const checkedInput = optionDiv.querySelector('input:checked');
    if (checkedInput) {
      const label = checkedInput.closest('label');
      if (label) label.classList.add(`${classPrefix}--selected`);
    }

    const valSpan = optionDiv.querySelector('.quick-view-multi-option__swatch-value');
    if (valSpan) valSpan.textContent = this.selectedValues[index] || '';
  }

  #checkAvailability(val, index) {
    if (!this.data) return false;
    const temp = [...this.selectedValues];
    temp[index] = val;
    return this.data.variants.some(v =>
      temp.slice(0, index + 1).every((s, i) => s === '' || s === v.splitValues[i]) && v.available
    );
  }

  #handleSelectionChange() {
    let needsRerender = false;

    this.optionNames.forEach((name, index) => {
      const optionDiv = this.querySelector(`[data-fieldset-index="${index}"]`);
      const valSpan = optionDiv?.querySelector('.quick-view-multi-option__swatch-value');
      if (valSpan) valSpan.textContent = this.selectedValues[index] || '';

      optionDiv?.querySelectorAll('input').forEach(input => {
        input.dataset.currentChecked = (input.value === this.selectedValues[index]) ? 'true' : 'false';
      });

      if (index > 0) {
        const eligibleValues = this.#getUniqueValuesAtIndex(index);
        const currentVal = this.selectedValues[index] ?? '';

        if (!eligibleValues.includes(currentVal)) {
          const firstAvailable = eligibleValues.find((/** @type {string} */ val) => {
            const temp = [...this.selectedValues.slice(0, index), val];
            return this.data.variants.some((/** @type {any} */ v) => temp.every((s, i) => s === v.splitValues[i]) && v.available);
          });
          this.selectedValues[index] = firstAvailable ?? eligibleValues[0] ?? '';
          needsRerender = true;
        }
      }
    });

    if (needsRerender) this.render();

    this.updateAvailability();

    if (this.selectedValues.every(v => v !== '')) {
      this.#dispatchSelectionChange();
    }
  }

  #dispatchSelectionChange() {
    const matchingVariant = this.data?.variants.find((/** @type {any} */ v) =>
      v.splitValues.every((/** @type {string} */ sv, /** @type {number} */ i) => sv === this.selectedValues[i])
    );
    const optionValueIds = this.#getSelectedOptionValueIds();

    this.dispatchEvent(new CustomEvent('quick-view-multi-option-change', {
      bubbles: true,
      detail: {
        selectedValues: [...this.selectedValues],
        combinedValue: this.selectedValues.join('/'),
        matchingVariant,
        optionValueIds,
        productId: this.productId
      }
    }));
  }

  #getSelectedOptionValueIds() {
    if (!this.data) return [];

    const ids = [];
    const combinedMultiOptionValue = this.selectedValues
      .slice(0, this.data.multiOptionIndices?.length || 0)
      .join('/');

    if (combinedMultiOptionValue && this.data.optionsMap[combinedMultiOptionValue]) {
      ids.push(this.data.optionsMap[combinedMultiOptionValue]);
    }

    const multiOptionCount = this.data.multiOptionIndices?.length || 0;
    for (let i = multiOptionCount; i < this.selectedValues.length; i++) {
      const optionName = this.optionNames[i];
      const value = this.selectedValues[i];
      if (optionName && value) {
        const metadata = this.data.valueMetadata?.[optionName]?.[value];
        if (metadata?.id) ids.push(metadata.id);
      }
    }

    return ids;
  }

  updateAvailability() {
    this.querySelectorAll('[data-fieldset-index]').forEach(optionDiv => {
      const opDiv = /** @type {HTMLElement} */ (optionDiv);
      const index = parseInt(opDiv.dataset.fieldsetIndex || '0');
      const optionName = this.optionNames[index] || '';
      const optionType = this.#getOptionType(optionName, index);
      const classPrefix = this.#getClassPrefix(optionType);

      opDiv.querySelectorAll('input').forEach(input => {
        const isAvailable = this.#checkAvailability(input.value, index);
        const icon = input.closest('.quick-view__swatch-wrapper')?.querySelector('.quick-view__swatch-icon--ring');
        const label = input.closest('label');

        input.dataset.optionAvailable = isAvailable.toString();

        if (!isAvailable) {
          input.setAttribute('aria-disabled', 'true');
          if (icon) icon.classList.remove('hidden');
          if (label) label.classList.add(`${classPrefix}--unavailable`, 'swatch-unavailable');
        } else {
          input.removeAttribute('aria-disabled');
          if (icon) icon.classList.add('hidden');
          if (label) label.classList.remove(`${classPrefix}--unavailable`, 'swatch-unavailable');
        }
      });
    });
  }

  getMatchingVariant() {
    if (!this.data || !this.selectedValues.every(v => v !== '')) return null;
    return this.data.variants.find((/** @type {any} */ v) =>
      v.splitValues.every((/** @type {string} */ sv, /** @type {number} */ i) => sv === this.selectedValues[i])
    );
  }

  getSelectedValues() {
    return [...this.selectedValues];
  }

  getOptionNames() {
    return [...this.optionNames];
  }
}

if (!customElements.get('quick-view-multi-option')) {
  customElements.define('quick-view-multi-option', QuickViewMultiOption);
}

export { QuickViewMultiOption };
